import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import WfhStatusBadge from './WfhStatusBadge';
import { WFH_STATUS, WFH_HALF_DAY_TYPE_LABELS } from '../constants';

const WfhTable = ({
  wfhRequests,
  onCancelWfh,
  onEditWfh,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const getHalfDayLabel = (wfh) => {
    if (!wfh.isHalfDay) return 'Full Day';
    return WFH_HALF_DAY_TYPE_LABELS[wfh.halfDayType] || wfh.halfDayType;
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {showEmployee && <TableCell>Employee</TableCell>}
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Days</TableCell>
              <TableCell>Half Day</TableCell>
              <TableCell>Status</TableCell>
              {showEmployee && <TableCell>Reviewed By</TableCell>}
              {showEmployee && <TableCell>Reviewed Date</TableCell>}
              <TableCell sx={{ pl: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wfhRequests && wfhRequests.length > 0 ? (
              wfhRequests.map((wfh) => (
                <TableRow key={wfh.id} hover>
                  {showEmployee && (
                    <TableCell>{wfh.employee?.userName || wfh.employee?.email || '-'}</TableCell>
                  )}
                  <TableCell>{formatDate(wfh.startDate)}</TableCell>
                  <TableCell>{formatDate(wfh.endDate)}</TableCell>
                  <TableCell>{wfh.totalDays}</TableCell>
                  <TableCell>{getHalfDayLabel(wfh)}</TableCell>
                  <TableCell>
                    <WfhStatusBadge status={wfh.status} />
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {wfh.approver && wfh.approver.id === currentUserId
                        ? 'Me'
                        : wfh.approver?.userName || wfh.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(wfh.reviewedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/wfh/${wfh.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {!showEmployee && wfh.status === WFH_STATUS.PENDING && wfh.userId === currentUserId && (
                        <>
                          {checkComponentPermission('UPDATE_WFH') && (
                            <Tooltip title="Edit WFH">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditWfh(wfh.id);
                                }}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {checkComponentPermission('DELETE_WFH') && (
                            <Tooltip title="Delete WFH">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelWfh(wfh.id);
                                }}
                              >
                                <Iconify icon="material-symbols:delete" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showEmployee ? 9 : 6} align="center">
                  No WFH requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

WfhTable.propTypes = {
  wfhRequests: PropTypes.array.isRequired,
  onCancelWfh: PropTypes.func,
  onEditWfh: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};

export default WfhTable;
